
const AWS = require('aws-sdk');
const { sprintf } = require("sprintf-js");
const { SuccessResponse, ErrorResponse, ERROR } = require("./response");
const { Region } = require("./env");

let IS_TESTING = false;
let VERB = false;

let CONSISTENT_READS = false;

if (process.env.CA_DYNAMO_DB_DEBUG) {
  VERB = process.env.CA_DYNAMO_DB_DEBUG;
}

if (process.env.CA_DYNAMO_DB_CONSISTENT_READS) {
  CONSISTENT_READS = true;
}

const SET_CONSISTENT_READS = sr => {
  CONSISTENT_READS = sr;
}

const SET_TESTING = () => {
  IS_TESTING = true;
}

const SET_VERB = verb => {
  VERB = verb;
}

const create_client = (creds) => {

  if (IS_TESTING) {
    return new AWS.DynamoDB({
      endpoint: 'localhost:8000',
      sslEnabled: false,
      region: 'local-env',
    });
  }

  if (creds) {
    if (VERB) {
      console.log("create_client creds", creds);
    }
    return new AWS.DynamoDB(creds.to_service());
  }

  const region = Region.region;
  if (VERB) {
    console.log("create_client region", region);
  }
  if (region) {
    return new AWS.DynamoDB({ region });
  }
  return new AWS.DynamoDB();
}

const Batch = async (table, keyList) => {
  return await BatchC(null, table, keyList);
}
/**
 * Calls batchGetItem.
 *
 * @param creds {CaCredentials}
 * @param table {string} DynamoDB table
 * @param keyList {[Key]} Array of key objects
 */
const BatchC = async (creds, table, keyList) => {

  const dyn = create_client(creds);
  const params = {
    RequestItems: {
      [table]: {
        Keys: keyList.map(AWS.DynamoDB.Converter.marshall),
        ConsistentRead: CONSISTENT_READS,
      }
    }
  };

  try {
    if (VERB) {
      console.log("aws-dynamo: batchGetItem", JSON.stringify(params, null, 2));
    }
    const resp = await dyn.batchGetItem(params).promise();
    return SuccessResponse(resp.Responses[table].map(AWS.DynamoDB.Converter.unmarshall));
  } catch (err) {
    if (VERB) {
      console.log(err);
    }
    return ErrorResponse(err);
  }
};

const BatchAll = async (table, key_list) => {
  return await BatchAllC(null, table, key_list);
}

// Batch requests in groups of 100
const BatchAllC = async (creds, table, key_list) => {

  const CHUNK_SIZE = 100;
  let result = [];

  for(let i=0; i < key_list.length; i += CHUNK_SIZE) {
    let resp = await BatchC(creds, table, key_list.slice(i, i+100));
    if (resp.status === ERROR) {
      return resp;
    }
    result = result.concat(resp.payload);
  }
  return SuccessResponse(result);
}

const marshall = item => {

  if (Array.isArray(item)) {
    return item.map(valueConvert);
  }

  let result = {};

  Object.keys(item).forEach(key => {
    if (item[key] !== '' && item[key] !== null) {
      result[key] = valueConvert(item[key]);
    }
  });

  return result;
};


/**
 * {@link https://docs.aws.amazon.com/amazondynamodb/latest/APIReference/API_AttributeValue.html}
 */
const valueConvert = value => {
  if (typeof value === "string") {
    return {'S': value};
  } else if (Array.isArray(value)) {
    return { 'L': marshall(value) }
  } else if (typeof value === "object") {
    return { 'M': marshall(value) }
  } else if (Number.isInteger(value) || typeof value === "number") {
    return {'N': value.toString()}
  } else if (typeof value === "boolean") {
    return { 'BOOL': value }
  } else {
    throw new Error("Can't convert " + (typeof value));
  }
};

/**
 * Convert {@param num} to a number of the form aa.
 */
const TENS = 26;
const HUNDREDS = 26 * 26;
const CODE_A = 97;

const num_to_char = (num) => {

  const hun = String.fromCharCode(
    Math.floor(num / HUNDREDS) + CODE_A);
  num = num % HUNDREDS;
  const ten = String.fromCharCode(
    Math.floor(num / TENS) + CODE_A);
  num = num % TENS;
  const ones = String.fromCharCode(num + CODE_A);
  return hun + ten + ones;
}

const Update = async (table, key, data, remove_attributes, return_value) => {
  return await UpdateC(null, table, key, data, remove_attributes, return_value);
}

/**
 * Do an record upate of the specified key.
 *
 * @param creds {CaCredentials} Table name
 * @param table {String} Table name
 * @param key { { partition: String, id: String } } key
 * @param data {Object} Data to be written.
 * @param remove_attributes {[String]} attributes to delete.
 * @param return_value {String} [null, 'ALL_NEW']
 */
const UpdateC = async (
  creds, table, key, data, remove_attributes = [], return_value = null) => {
  let expressionList = [];
  let values = {};
  let vars = {};

  try {
    Object.keys(data).forEach((key, index) => {
      if (data[key] !== '' && data[key] !== null) {
        let var_sub = "#" + num_to_char(index);
        const safe_key = key.replace(/-/g, "").replace(/ /g, "");
        vars[var_sub] = key;
        expressionList.push(sprintf("%s = :%s", var_sub, safe_key));
        values[sprintf(":%s", safe_key)] = valueConvert(data[key]);
      }
    });
  } catch (err) {
    return ErrorResponse(err);
  }

  let expressionUpdate = "";

  if (expressionList.length > 0) {
    expressionUpdate += "SET " + expressionList.join(',') + " ";
  }
  if (remove_attributes.length > 0) {
    expressionUpdate += "REMOVE " + remove_attributes.join(",");
  }

  let params = {
    TableName: table,
    Key: AWS.DynamoDB.Converter.marshall(key),
    UpdateExpression: expressionUpdate,
  };
  if (return_value) {
    params.ReturnValues = return_value;
  }
  if (Object.keys(values).length > 0) {
    params.ExpressionAttributeValues = values;
  }
  if (Object.keys(vars).length > 0) {
    params.ExpressionAttributeNames = vars;
  }

  const dyn = create_client(creds);

  try {
    if (VERB) {
      console.log("aws-dynamo: updateItem", JSON.stringify(params, null, 2));
    }
    const resp = await dyn.updateItem(params).promise();
    if (return_value) {
      return SuccessResponse(AWS.DynamoDB.Converter.unmarshall(resp.Attributes));
    }
    return SuccessResponse(resp);
  } catch (err) {
    if (VERB) {
      console.log(err);
    }
    return ErrorResponse(err);
  }
};

const UpdateSS = async (table, item_key, add_items, remove_items) => {
  return await UpdateSSC(null, table, item_key, add_items, remove_items);
}

/**
 * Add/remove strings from a StringSet.
 *
 * @param creds {CaCredentials}
 * @param table {String}
 * @param item_key { { partition: string, id: string } }
 * @param add_items { { [string]: [ values ]} }
 * @param remove_items { { [att_name]: [ values ]} }
 */
const UpdateSSC = async (creds, table, item_key, add_items, remove_items) => {

  const expression_add_list = []
  const expression_remove_list = []
  const values = {}
  const attribute_names = {}
  let count = 0

  Object.keys(add_items).forEach(att => {
    const key_sub = "#" + num_to_char(count);
    count += 1;
    attribute_names[key_sub] = att;
    expression_add_list.push(sprintf("%s :%s", key_sub, att));
    values[sprintf(":%s", att)] = {
      SS: add_items[att],
    }
  })


  Object.keys(remove_items).forEach(att => {
    const key_sub = "#" + num_to_char(count);
    count += 1;
    attribute_names[key_sub] = att;
    expression_remove_list.push(sprintf("%s :%s", key_sub, att));
    values[sprintf(":%s", att)] = { SS: remove_items[att] };
  });

  let expression_update = ""

  if (expression_add_list.length > 0) {
    expression_update += "ADD " + expression_add_list.join(",");
  }

  if (expression_remove_list.length > 0) {
    expression_update += " DELETE " + expression_remove_list.join(",");
  }

  const params = {
    TableName: table,
    Key: AWS.DynamoDB.Converter.marshall(item_key),
    UpdateExpression: expression_update,
  };

  if (values) {
    params.ExpressionAttributeValues = values;
  }
  if (attribute_names) {
    params.ExpressionAttributeNames = attribute_names;
  }
  const dyn = create_client(creds);

  try {
    if (VERB) {
      console.log("aws-dynamo: updateItem", JSON.stringify(params, null, 2));
    }
    return SuccessResponse(await dyn.updateItem(params).promise());
  } catch (err) {
    if (VERB) {
      console.log(err);
    }
    return ErrorResponse(err);
  }
}

const UpdatePaths = async (table, key, paths, return_value = null) => {
  return await UpdatePathsC(null, table, key, paths, return_value);
}

/**
 * Same as update, but designed to update nested map values. If
 * the 'value' is left out, then the path is removed.
 *
 * @param creds {CaCredentials|null}
 * @param table {String}
 * @param key { partition: {String}, id: {String}}
 * @param paths [ {path: [String], value: <value> } ]
 * @param return_value {String} [null, 'ALL_NEW']
 */
const UpdatePathsC = async (creds, table, key, paths, return_value = null) => {

  const expression_list = [];
  const delete_attributes = [];
  const values = {};
  const vars = {};
  let index = 0;

  try {
    paths.forEach(({path, value}) => {

      const sub_paths = [];
      path.forEach(segment => {
        const var_sub = "#" + num_to_char(index++);
        sub_paths.push(var_sub);
        vars[var_sub] = segment;
      });

      if (value !== undefined) {
        const value_key = path.length === 0 ? "BLANK" : path.join("_").replace(/-/g, "");
        values[sprintf(":%s", value_key)] = valueConvert(value);
        expression_list.push(sprintf("%s = :%s", sub_paths.join("."), value_key));
      } else {
        delete_attributes.push(sub_paths.join("."));
      }
    });
  } catch (err) {
    return ErrorResponse(err);
  }

  let expression_update = "";
  if (expression_list.length > 0) {
    expression_update += "SET " + expression_list.join(",") + " ";
  }
  if (delete_attributes.length > 0) {
    expression_update += "REMOVE " + delete_attributes.join(",");
  }

  const params = {
    TableName: table,
    Key: AWS.DynamoDB.Converter.marshall(key),
    UpdateExpression: expression_update,
    ExpressionAttributeNames: vars,
  };
  if (Object.keys(values).length > 0) {
    params.ExpressionAttributeValues = values;
  }
  if (return_value) {
    params.ReturnValues = return_value;
  }

  const dyn = create_client(creds);

  try {
    if (VERB) {
      console.log("aws-dynamo: updateItem", JSON.stringify(params, null, 2));
    }
    const resp = await dyn.updateItem(params).promise();
    if (return_value) {
      return SuccessResponse(AWS.DynamoDB.Converter.unmarshall(resp.Attributes));
    }
    return SuccessResponse(resp);

  } catch (err) {
    if (VERB) {
      console.log(err);
    }
    return ErrorResponse(err);
  }
};

const BatchWrite = async (data) => {
  return await BatchWriteC(null, data);
}

/**
 * @param creds: {CaCredentials|null}
 * {@param data} is of the form:
 *   table_0: [ item0, item1, ... ],
 *   table_1: [ item0, item2, ... ],
 */
const BatchWriteC = async (creds, data) => {

  const dyn = create_client(creds);
  let params = {
    RequestItems: {}
  };

  for (let table in data) {
    let items = data[table];
    params.RequestItems[table] = items.map(item => {
      return {
        PutRequest: {
          Item: AWS.DynamoDB.Converter.marshall(item),
        }
      }
    });
  }

  try {
    if (VERB) {
      console.log("aws-dynamo: batchWriteItem", JSON.stringify(params, null, 2));
    }
    return SuccessResponse(await dyn.batchWriteItem(params).promise());
  } catch (err) {
    if (VERB) {
      console.log(err);
    }
    return ErrorResponse(err);
  }
};

const BatchWriteAll = async (table, data, num_threads=1, verb=false) => {
  return await BatchWriteAllC(null, table, data, num_threads, verb);
}

const BatchWriteAllC = async (creds, table, data, num_threads=1, verb=false) => {
  const chunk_size = 25;

  const chunked_data = [];

  for(let i=0; i < data.length; i += chunk_size) {
    chunked_data.push(data.slice(i, i + chunk_size));
  }

  while(chunked_data.length > 0) {
    const promises = [];

    let log = "";

    for(let i=0; i < num_threads && chunked_data.length > 0; i++) {
      log += sprintf("Chunk:%d ", chunked_data[0].length);
      promises.push(BatchWriteC(creds, { [table]: chunked_data.shift() } ));
    }

    if (verb) {
      console.log("Executing", log, "of",
        Math.ceil(chunked_data.length / num_threads));
    }
    const resps = await Promise.all(promises);
    for(let i=0; i < resps.length; i++) {
      if (resps[i].status === ERROR) {
        return resps[i];
      }
    }
  }
  return SuccessResponse("OK");
}

const QueryKeyAttributes = () => {
  return {
    Select: 'SPECIFIC_ATTRIBUTES',
    ProjectionExpression: 'id',
  }
}

const QueryRangeFirstLast = (partition, do_first) => {
  return {
    KeyConditionExpression: "#part = :partition",
    ExpressionAttributeNames: {
      "#part": "partition",
    },
    ExpressionAttributeValues: {
      ":partition": { S: partition },
    },
    Limit: 1,
    ScanIndexForward: do_first,
  }
}

const QueryRangeIdPrefixFirstLast = (partition, id_prefix, do_first) => {
  return {
    KeyConditionExpression: "#part = :partition and begins_with(#sort, :sort)",
    ExpressionAttributeNames:{
      "#part": "partition",
      "#sort": "id",
    },
    ExpressionAttributeValues: {
      ":partition": { S: partition },
      ":sort": { S: id_prefix },
    },
    ScanIndexForward: do_first,
  }
}

const OP = {
  LT: "<",
  LTE: "<=",
  GT: ">",
  GTE: ">=",
}

// Creates parameter overrides for executing a Query to return
// items relative to 'id'. LT, LTE, GT, GTE. See 'OP' above.
const QueryRangeRelativeTo = (partition, id, op) => {
  return {
    KeyConditionExpression: sprintf("#part = :partition AND #id %s :id", op),
    ExpressionAttributeNames: {
      "#part": "partition",
      "#id": "id",
    },
    ExpressionAttributeValues: {
      ":partition": { S: partition },
      ":id": { S: id },
    }
  }
}

const QueryIndexRangeRelativeTo = (index_name, partition, value, op) => {
  return {
    IndexName: index_name,
    KeyConditionExpression: sprintf("#part = :partition AND #sort %s :value", op),
    ExpressionAttributeNames: {
      "#part": "partition",
      "#sort": index_name,
    },
    ExpressionAttributeValues: {
      ":partition": { S: partition },
      ":value": { S: value },
    },
    ConsistentRead: false,
  }
}

const QueryIndexRangeFirstLast = (index_name, partition, do_first) => {
  return {
    IndexName: index_name,
    KeyConditionExpression: "#part = :partition",
    ExpressionAttributeNames: {
      "#part": "partition",
    },
    ExpressionAttributeValues: {
      ":partition": { S: partition },
    },
    Limit: 1,
    ScanIndexForward: do_first,
    ConsistentRead: false,
  }
}

const QueryIndexSortEquals = (index_name, partition, sort) => {
  return {
    IndexName: index_name,
    KeyConditionExpression: "#part = :partition and #sort = :sort",
    ExpressionAttributeNames:{
      "#part": "partition",
      "#sort": index_name,
    },
    ExpressionAttributeValues: {
      ":partition": { S: partition },
      ":sort": { S: sort },
    },
    ConsistentRead: false,
  }
}

const QueryIndexSortBeginsWith = (index_name, partition, sort) => {
  return {
    IndexName: index_name,
    KeyConditionExpression: "#part = :partition and begins_with(#sort, :sort)",
    ExpressionAttributeNames:{
      "#part": "partition",
      "#sort": index_name,
    },
    ExpressionAttributeValues: {
      ":partition": { S: partition },
      ":sort": { S: sort },
    },
    ConsistentRead: false,
  }
}

const Query = async (table, partition, page_size, lastEvalKey,
                      id=undefined,
                      id_prefix=undefined,
                      param_override = {}) => {
  return await QueryC(
    null, table, partition, page_size, lastEvalKey, id, id_prefix, param_override);
}

/**
 * Do a query.
 * @param creds {CaCredentails|null}
 * @param table Dynamodb table
 * @param partition The partition to query over
 * @param page_size Max items to read. If {@code undefined},
 *   it is ignored.
 * @param lastEvalKey Continuation data
 * @param id {String} id equals
 * @param id_prefix {String} If defined, defines an id prefix
 * @param param_override {Object} Modifiers to the request.
 */
const QueryC = async (creds, table, partition, page_size, lastEvalKey,
               id=undefined,
               id_prefix=undefined,
               param_override = {}) => {

  param_override = { ...param_override};

  const dyn = create_client(creds);
  const params = {
    TableName: table,
    KeyConditionExpression: "#part = :partition",
    ExpressionAttributeNames:{
      "#part": "partition",
    },
    ExpressionAttributeValues: {
      ":partition": {
        S: partition
      },
    },
    ConsistentRead: CONSISTENT_READS,
  };

  if (id) {
    params.ExpressionAttributeNames['#id'] = 'id';
    params.ExpressionAttributeValues[':id'] = { S: id };
    params.KeyConditionExpression += " AND #id = :id";
  }

  if (id_prefix) {
    params.ExpressionAttributeNames['#id'] = 'id';
    params.ExpressionAttributeValues[':prefix'] = { S: id_prefix };
    params.KeyConditionExpression += " AND begins_with(#id, :prefix)";
  }

  if (page_size) {
    params.Limit = page_size;
  }
  if (lastEvalKey) {
    params.ExclusiveStartKey = lastEvalKey;
  }
  Object.assign(params, param_override);

  try {
    if (VERB) {
      console.log("aws-dynamo: query", JSON.stringify(params, null, 2));
    }
    const resp = await dyn.query(params).promise();
    return SuccessResponse({
      items: resp.Items.map(AWS.DynamoDB.Converter.unmarshall),
      page_token: resp.LastEvaluatedKey,
    })
  } catch (err) {
    if (VERB) {
      console.log(err);
    }
    return ErrorResponse(err);
  }
};

const QueryAll = async (
  table, partition, page_size, id, id_prefix, param_override = {}) => {
  return await QueryAllC(null, table, partition, page_size, id, id_prefix, param_override);
}


const QueryAllC = async (
  creds, table, partition, page_size, id, id_prefix, param_override = {}) => {

  let items = [];
  let resp, page_token;

  while(true) {
    resp = await QueryC(creds, table, partition, page_size, page_token,
      id, id_prefix, param_override);
    if (resp.status === ERROR) {
      return resp;
    }

    items = items.concat(resp.payload.items);
    page_token = resp.payload.page_token;

    if (!page_token) {
      break;
    }
  }

  return SuccessResponse(items);
}

const QueryIndex = async (
  table, partition, index_name, id, lastEvalKey, param_override = {}) => {
  return await QueryIndexC(null, table, partition, index_name, id, lastEvalKey, param_override);
}

const QueryIndexC = async (
  creds, table, partition, index_name, id, lastEvalKey, param_override = {}) => {

  const dyn = create_client(creds);
  const has_sort = !!id;

  const params = {
    TableName: table,
    IndexName: index_name,
    KeyConditionExpression: has_sort ? "#part = :partition and #sort = :sort" : "#part = :partition",
    ExpressionAttributeNames:{
      "#part": "partition",
    },
    ExpressionAttributeValues: {
      ":partition": {
        S: partition
      },
    },
  };

  if (has_sort) {
    params.ExpressionAttributeNames['#sort'] = index_name;
    params.ExpressionAttributeValues[':sort'] = { S: id };
  }

  if (lastEvalKey) {
    params.ExclusiveStartKey = lastEvalKey;
  }
  Object.assign(params, param_override);

  let resp;

  try {
    if (VERB) {
      console.log("aws-dynamo: query", JSON.stringify(params, null, 2));
    }
    resp = await dyn.query(params).promise();
    return SuccessResponse({
      items: resp.Items ? resp.Items.map(AWS.DynamoDB.Converter.unmarshall) : [],
      page_token: resp.LastEvaluatedKey,
    })
  } catch (err) {
    if (VERB) {
      console.log(err);
    }
    return ErrorResponse(err);
  }
}

const BatchDelete = async (data) => {
  return await BatchDeleteC(null, data);
}

/**
 * @param creds {CaCredentials|null}
 * {@param data} is of the form:
 *   table_0: [ key0, key1, ... ],
 *   table_1: [ key0, key1, ... ],
 */
const BatchDeleteC = async (creds, data) => {

  const params = {
    RequestItems: {},
  };

  for (let table in data) {
    let keys = data[table];
    params.RequestItems[table] = keys.map(key => {
      return {
        DeleteRequest: {
          Key: AWS.DynamoDB.Converter.marshall(key),
        }
      }
    });
  }

  const dyn = create_client(creds);

  try {
    if (VERB) {
      console.log("aws-dynamo: batchWriteItem", JSON.stringify(params, null, 2));
    }
    return SuccessResponse(await dyn.batchWriteItem(params).promise());
  } catch (err) {
    if (VERB) {
      console.log(err);
    }
    return ErrorResponse(err);
  }
};

const BatchDeleteAll = async (table, keys) => {
  return await BatchDeleteAllC(null, table, keys);
}

const BatchDeleteAllC = async (creds, table, keys) => {
  const chunk_size = 25;

  for(let i=0; i < keys.length; i += chunk_size) {
    let resp = await BatchDeleteC(creds, {[table]: keys.slice(i, i + chunk_size)});
    if (resp.status === ERROR) {
      return resp;
    }
  }
  return SuccessResponse("DONE");
}

const IncCounter = async (table, key, counter_attribute, amt) => {
  return await IncCounterC(null, table, key, counter_attribute, amt);
}

const IncCounterC = async (creds, table, key, counter_attribute, amt) => {

  let params;

  try {
    params = {
      TableName: table,
      Key: AWS.DynamoDB.Converter.marshall(key),
      UpdateExpression: "ADD #counter :increment",
      ExpressionAttributeNames: {'#counter': counter_attribute},
      ExpressionAttributeValues: {':increment': valueConvert(amt)},
      ReturnValues: "ALL_NEW",
    };
  } catch (err) {
    return ErrorResponse(err);
  }

  const dyn = create_client(creds);

  try {
    if (VERB) {
      console.log("aws-dynamo: updateItem", JSON.stringify(params, null, 2));
    }
    const resp = await dyn.updateItem(params).promise();
    return SuccessResponse(
      AWS.DynamoDB.Converter.unmarshall(resp.Attributes));
  } catch (err) {
    if (VERB) {
      console.log(err);
    }
    return ErrorResponse(err);
  }
};

const ScanAllKeys = async (table) => {
  return await ScanAllKeysC(null, table);
}

const ScanAllKeysC = async (creds, table) => {
  let keys = [];
  const params = {
    AttributesToGet: [ "partition", "id" ],
    TableName: table,
    ConsistentRead: true,
  }
  const dyn = create_client(creds);

  while(true) {

    let resp;

    try {
      if (VERB) {
        console.log("aws-dynamo: scan", JSON.stringify(params, null, 2));
      }
      resp = await dyn.scan(params).promise();
      keys = keys.concat(resp.Items.map(AWS.DynamoDB.Converter.unmarshall));
    } catch (err) {
      if (VERB) {
        console.log(err);
      }
      return ErrorResponse(err);
    }

    if (!resp.LastEvaluatedKey) {
      break;
    }

    params.ExclusiveStartKey = resp.LastEvaluatedKey;
  }

  return SuccessResponse(keys);
}

const CopyTableC = async (creds, table_from, table_to) => {

  const params = {
    TableName: table_from,
    ConsistentRead: true,
  }
  const dyn = create_client(creds);

  while(true) {
    let resp;

    try {
      if (VERB) {
        console.log("aws-dynamo: scan", JSON.stringify(params, null, 2));
      }
      resp = await dyn.scan(params).promise();
      const items = resp.Items.map(AWS.DynamoDB.Converter.unmarshall);

      const w_resp = await BatchWriteAll(table_to, items);
      if (w_resp.status === Status.ERROR) {
        return w_resp;
      }

    } catch (err) {
      if (VERB) {
        console.log(err);
      }
      return ErrorResponse(err);
    }

    if (!resp.LastEvaluatedKey) {
      break;
    }

    params.ExclusiveStartKey = resp.LastEvaluatedKey;
  }

  return SuccessResponse("DONE");
}

module.exports = {
  Batch, BatchC, BatchAll, BatchAllC, Update, UpdateC,
  BatchWrite, BatchWriteC, BatchWriteAll, BatchWriteAllC,
  UpdatePaths, UpdatePathsC, Query, QueryC, QueryRangeRelativeTo,
  QueryIndexRangeRelativeTo, QueryRangeFirstLast, QueryRangeIdPrefixFirstLast, OP,
  QueryIndexSortEquals, QueryIndexSortBeginsWith, QueryIndexRangeFirstLast,
  QueryKeyAttributes, BatchDelete, BatchDeleteC, UpdateSS, UpdateSSC,
  IncCounter, IncCounterC, QueryAll, QueryAllC,
  BatchDeleteAll, BatchDeleteAllC, QueryIndex, QueryIndexC,
  SET_TESTING, create_client, ScanAllKeys, ScanAllKeysC, SET_VERB,
  CopyTableC, SET_CONSISTENT_READS,
};
